import React, { useEffect, useRef } from 'react';
import './DivineParticles.scss';

// Floating golden embers / Om glyphs animation drawn on a canvas.
// Props:
//   density - number of particles (default 26)
//   className - extra classes to apply
const DivineParticles = ({ density = 26, className = '' }) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext('2d');
    let width = (canvas.width = canvas.offsetWidth);
    let height = (canvas.height = canvas.offsetHeight);
    let raf = 0;
    let loopHandle = 0;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    // Touch/coarse-pointer devices get a single static glow instead of a
    // per-frame canvas loop — removes jank and battery drain on phones.
    const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    const animate = !reduceMotion && finePointer;

    // Scale particles to the visible area so narrow phones don't over-render.
    const effDensity = Math.max(8, Math.min(density, Math.round(density * (width / 1400) + 4)));

    const particles = Array.from({ length: effDensity }, () => ({
      x: Math.random() * width,
      y: Math.random() * height,
      r: 0.8 + Math.random() * 1.9,
      vx: (Math.random() - 0.5) * 0.28,
      vy: -0.12 - Math.random() * 0.4,
      alpha: 0.15 + Math.random() * 0.75,
      pulse: Math.random() * Math.PI * 2,
      om: Math.random() < 0.3,
    }));

    const drawOm = (x, y, size, alpha) => {
      ctx.font = `${size}px "Cormorant Garamond", serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.globalAlpha = alpha;
      ctx.fillStyle = 'rgba(229, 162, 60, 0.9)';
      ctx.fillText('ॐ', x, y);
      ctx.globalAlpha = 1;
    };

    const drawStatic = () => {
      ctx.clearRect(0, 0, width, height);
      particles.forEach((p) => {
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255, 214, 140, ${p.alpha * 0.5})`;
        ctx.fill();
      });
    };

    const tick = () => {
      ctx.clearRect(0, 0, width, height);
      particles.forEach((p) => {
        p.x += p.vx;
        p.y += p.vy;
        p.pulse += 0.03;
        if (p.y < -20) { p.y = height + 20; p.x = Math.random() * width; }
        if (p.x < -20) p.x = width + 20;
        if (p.x > width + 20) p.x = -20;

        const a = p.alpha * (0.6 + 0.4 * Math.sin(p.pulse));
        if (p.om) {
          if (Math.random() < 0.12) drawOm(p.x, p.y, p.r * 11, a * 0.4);
        } else {
          ctx.beginPath();
          ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(255, 214, 140, ${a})`;
          ctx.fill();
        }
      });
      raf = requestAnimationFrame(tick);
    };

    const onResize = () => {
      width = canvas.width = canvas.offsetWidth;
      height = canvas.height = canvas.offsetHeight;
    };
    window.addEventListener('resize', onResize);

    // Only animate while the canvas is actually on screen — this removes
    // constant CPU burn (and jank) once the section scrolls out of view.
    let visible = true;
    const start = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(tick);
    };
    const stop = () => cancelAnimationFrame(raf);
    const io =
      'IntersectionObserver' in window
        ? new IntersectionObserver(
            (entries) => {
              const isVisible = entries[0].isIntersecting;
              if (isVisible && !visible) start();
              if (!isVisible && visible) stop();
              visible = isVisible;
            },
            { rootMargin: '20px' }
          )
        : null;
    if (io) io.observe(canvas);

    if (!animate) {
      drawStatic();
    } else {
      // Draw a static frame immediately, then defer the per-frame loop until
      // the main thread is idle so LCP (hero text) isn't starved at load.
      drawStatic();
      const beginLoop = () => {
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(tick);
      };
      if (typeof requestIdleCallback === 'function') {
        loopHandle = window.requestIdleCallback(beginLoop, { timeout: 1200 });
      } else {
        loopHandle = window.setTimeout(beginLoop, 600);
      }
    }
    return () => {
      cancelAnimationFrame(raf);
      if (typeof requestIdleCallback === 'function') cancelIdleCallback(loopHandle);
      else clearTimeout(loopHandle);
      if (io) io.disconnect();
      window.removeEventListener('resize', onResize);
    };
  }, [density]);

  return <canvas ref={canvasRef} className={`divine-particles ${className}`} aria-hidden="true" />;
};

export default DivineParticles;
